import React from 'react';
import { Button, Checkbox, Icon, Label, Message, Modal, Table } from 'semantic-ui-react';
import { format } from 'date-fns';
import SmoothModal from './components/SmoothModal';
import colors from '../utils/colors';

const names = (ids, dict) => ids.length === 0 ? <span style={{ color: colors.midGray }}>nobody</span> : ids.map(id => dict[id] ?? 'Unknown').join(', ');
const listText = (items) => items.length <= 1 ? (items[0] ?? '') : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;

/** Same cadence as the service templates' scheduled alerts (functions/src/services/ServiceReminders.ts) */
export const FIRST_TEXT_WEEKS = 4;
export const FIRST_TEXT_DAYS = 29;
export const REMINDER_DAYS = [22, 15, 8];

export const cadenceText = (roleNames) =>
	`${listText(roleNames)} ${roleNames.length === 1 ? 'is' : 'are'} texted automatically: the first text goes out ${FIRST_TEXT_WEEKS} weeks ahead `
	+ `(${FIRST_TEXT_DAYS} days before, the Saturday morning for a Sunday service), then a reminder every week `
	+ `(${listText(REMINDER_DAYS.map(String))} days before) until they confirm or decline.`;

/** Shows exactly what a Services save will change (from the server's dry run) before anything is written. */
export default class ServicesPreviewPopup extends React.Component {
	constructor(props) {
		super(props);
		this.state = { notify: true };
	}

	render() {
		const { open, preview, memberNamesDict, saving, onClose, onConfirm, notifyRoleNames = [] } = this.props;
		const { notify } = this.state;

		const results = preview?.results ?? [];
		const ok = results.filter(r => r.status === 'updated' || r.status === 'created');
		const problems = results.filter(r => r.status === 'error' || r.status === 'conflict');
		const texted = [...new Set(ok.flatMap(r => r.texted))];
		const autoDeclined = [...new Set(ok.flatMap(r => r.autoDeclined ?? []))];
		// a notification is needed to text people, and also to auto-decline pending people who have a blockout
		const canNotify = texted.length > 0 || autoDeclined.length > 0;
		const noPhone = [...new Set(ok.flatMap(r => r.noPhone ?? []))];
		const blockouts = ok.filter(r => r.blockouts?.length > 0);
		// more than 4 weeks out: left to the service's automatic 4-weeks-out text
		const later = ok.filter(r => r.scheduled?.length > 0);

		return (
			<SmoothModal open={open} size='large' onClose={saving ? undefined : onClose} header='Review changes'>
				<Modal.Content scrolling>
					{problems.length > 0 &&
						<Message error>
							<Message.Header>{problems.length} service{problems.length === 1 ? '' : 's'} can't be saved</Message.Header>
							{problems.map(r => <div key={r.key}>{r.label}, {r.day}: {r.message}</div>)}
						</Message>
					}

					{ok.length === 0 ?
						<Message>Nothing to change.</Message>
						:
						<Table celled compact unstackable>
							<Table.Header>
								<Table.Row>
									<Table.HeaderCell>Service</Table.HeaderCell>
									<Table.HeaderCell>Role</Table.HeaderCell>
									<Table.HeaderCell>Before</Table.HeaderCell>
									<Table.HeaderCell>After</Table.HeaderCell>
								</Table.Row>
							</Table.Header>
							<Table.Body>
								{ok.map(r => r.diffs.map((d, idx) =>
									<Table.Row key={`${r.key}_${d.roleId}`}>
										{idx === 0 &&
											<Table.Cell rowSpan={r.diffs.length} verticalAlign='top'>
												<b>{r.label}</b>
												<div>{r.start ? format(new Date(r.start), 'EEE, MMM d, h:mm a') : r.day}</div>
												{r.status === 'created' && <Label size='mini' color='blue' style={{ marginTop: 4 }}>Creates event</Label>}
											</Table.Cell>
										}
										<Table.Cell verticalAlign='top'>
											<b>{d.roleName}</b>
											<div style={{ color: colors.darkGray, fontSize: 12 }}>
												{d.fieldNames.length} field{d.fieldNames.length === 1 ? '' : 's'}: {[...new Set(d.fieldNames)].join(', ')}
											</div>
										</Table.Cell>
										<Table.Cell verticalAlign='top'>
											{names(d.before.memberIds, memberNamesDict)}
											{d.before.mixed && <Label size='mini' color='orange' style={{ marginLeft: 6 }}>Mixed</Label>}
											{d.notesChanged && (d.before.notes || d.before.notesValues?.length > 0) &&
												<div style={{ fontStyle: 'italic', whiteSpace: 'pre-wrap' }}>{d.before.notes || d.before.notesValues.join(' / ')}</div>
											}
										</Table.Cell>
										<Table.Cell verticalAlign='top' positive>
											{names(d.after.memberIds, memberNamesDict)}
											{d.notesChanged && <div style={{ fontStyle: 'italic', whiteSpace: 'pre-wrap' }}>{d.after.notes || <span style={{ color: colors.midGray }}>(notes cleared)</span>}</div>}
										</Table.Cell>
									</Table.Row>
								))}
							</Table.Body>
						</Table>
					}

					{blockouts.length > 0 &&
						<Message warning>
							<Message.Header>Blockout conflicts</Message.Header>
							{blockouts.map(r => <div key={r.key}>
								{r.label}, {r.day}: {names(r.blockouts, memberNamesDict)} marked themselves unavailable.
								{notify && r.autoDeclined?.length > 0 && ` ${names(r.autoDeclined, memberNamesDict)} will be marked declined instead of texted.`}
							</div>)}
						</Message>
					}

					{ok.length > 0 && notifyRoleNames.length > 0 &&
						<Message info size='small' style={{ marginBottom: 10 }}>
							<Message.Header>When people are texted</Message.Header>
							<p style={{ marginTop: 4 }}>{cadenceText(notifyRoleNames)} When a service's {FIRST_TEXT_WEEKS}-week text hasn't gone out yet, nobody on it is texted now: that text asks them.</p>
							{later.length > 0 &&
								<div data-testid='svc-later'>
									<b>Not texted now (their automatic first text is still to come):</b>
									{later.map(r =>
										<div key={r.key}>
											{r.label}, {r.start ? format(new Date(r.start), 'EEE, MMM d') : r.day}: {names(r.scheduled, memberNamesDict)}
											{r.scheduledAt && <span style={{ color: colors.darkGray }}> (first text {format(new Date(r.scheduledAt), 'EEE, MMM d')})</span>}
										</div>
									)}
								</div>
							}
						</Message>
					}

					{ok.length > 0 &&
						<div style={{ padding: 12, border: `1px solid ${colors.border}`, borderRadius: 4 }}>
							<Checkbox
								toggle
								checked={notify}
								disabled={!canNotify}
								onChange={(e, { checked }) => this.setState({ notify: checked })}
								label={texted.length === 0
									? (later.length > 0
										? `Nobody to text now (nobody new on a service in the next ${FIRST_TEXT_WEEKS} weeks)`
										: 'Nobody to text (everyone here was already asked to confirm)')
									: `Text people now for services in the next ${FIRST_TEXT_WEEKS} weeks who haven't been asked to confirm yet (${texted.length}): ${names(texted, memberNamesDict)}`}
							/>
							{noPhone.length > 0 &&
								<div style={{ marginTop: 6, color: colors.darkGray, fontSize: 12 }}>
									<Icon name='info circle' />No phone number, so no text: {names(noPhone, memberNamesDict)}
								</div>
							}
						</div>
					}
				</Modal.Content>

				<Modal.Actions>
					<Button onClick={onClose} disabled={saving}>Back</Button>
					<Button primary disabled={ok.length === 0 || saving} loading={saving} onClick={() => onConfirm(notify && canNotify)}>
						Save {ok.length} service{ok.length === 1 ? '' : 's'}{notify && texted.length > 0 ? ` and text ${texted.length}` : ''}
					</Button>
				</Modal.Actions>
			</SmoothModal>
		);
	}
}
